import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function toYMD(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function parseYMD(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

function formatDisplay(s: string) {
  const d = parseYMD(s);
  if (!d) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function addMonths(date: Date, n: number) {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return d;
}

function calendarDays(year: number, month: number) {
  const first = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const rows: (number | null)[][] = [];
  let day = 1 - first;
  while (day <= daysInMonth) {
    const row: (number | null)[] = [];
    for (let col = 0; col < 7; col++) {
      row.push(day >= 1 && day <= daysInMonth ? day : null);
      day++;
    }
    rows.push(row);
  }
  return rows;
}

interface Props {
  startDate: string;
  endDate: string;
  onApply: (start: string, end: string) => void;
  maxDate?: string;
}

const DateRangePicker: React.FC<Props> = ({ startDate, endDate, onApply, maxDate }) => {
  const today = toYMD(new Date());
  const max = maxDate || today;

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState('');
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');
  const [draft, setDraft] = useState({ start: startDate, end: endDate });
  const [leftMonth, setLeftMonth] = useState(() => {
    const base = parseYMD(startDate) || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!open) return;
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    const handleClick = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node) && !popoverRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const rightMonth = addMonths(leftMonth, 1);

  const handleDayClick = (ymd: string) => {
    if (ymd > max) return;
    if (selecting === 'start') {
      setDraft({ start: ymd, end: '' });
      setSelecting('end');
    } else {
      if (ymd < draft.start) {
        setDraft({ start: ymd, end: draft.start });
      } else {
        setDraft(d => ({ ...d, end: ymd }));
      }
      setSelecting('start');
    }
  };

  const handleApply = () => {
    if (draft.start && draft.end) {
      onApply(draft.start, draft.end);
      setOpen(false);
    }
  };

  const handleClear = () => {
    setDraft({ start: '', end: '' });
    setSelecting('start');
  };

  const effectiveEnd = draft.end || hovered;

  const inRange = (ymd: string) => {
    if (!draft.start || !effectiveEnd) return false;
    const lo = draft.start < effectiveEnd ? draft.start : effectiveEnd;
    const hi = draft.start < effectiveEnd ? effectiveEnd : draft.start;
    return ymd > lo && ymd < hi;
  };

  const isStart = (ymd: string) => ymd === draft.start;
  const isEnd = (ymd: string) => ymd === (draft.end || (selecting === 'end' ? hovered : ''));

  const renderMonth = (base: Date) => {
    const year = base.getFullYear();
    const month = base.getMonth();
    const rows = calendarDays(year, month);
    return (
      <div className="w-64">
        <div className="flex items-center justify-between mb-3 px-1">
          <span className="text-sm font-semibold text-slate-800 dark:text-white">
            {MONTHS[month]} {year}
          </span>
        </div>
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map(d => (
            <div key={d} className="text-center text-[10px] font-semibold text-slate-400 py-1">{d}</div>
          ))}
        </div>
        {rows.map((row, ri) => (
          <div key={ri} className="grid grid-cols-7">
            {row.map((day, ci) => {
              if (!day) return <div key={ci} />;
              const ymd = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const isDisabled = ymd > max;
              const start = isStart(ymd);
              const end = isEnd(ymd);
              const range = inRange(ymd);
              return (
                <div
                  key={ci}
                  onMouseEnter={() => selecting === 'end' && !isDisabled && setHovered(ymd)}
                  onMouseLeave={() => setHovered('')}
                  onClick={() => !isDisabled && handleDayClick(ymd)}
                  className={`
                    relative flex items-center justify-center h-8 text-xs cursor-pointer select-none transition-colors
                    ${isDisabled ? 'opacity-30 cursor-not-allowed' : ''}
                    ${range ? 'bg-primary-500/10 dark:bg-primary-500/15' : ''}
                    ${start || end ? 'z-10' : ''}
                  `}
                >
                  <span className={`
                    w-7 h-7 flex items-center justify-center rounded-full font-medium transition-colors
                    ${start || end ? 'bg-primary-500 text-white font-semibold' : ''}
                    ${!start && !end && !isDisabled ? 'hover:bg-primary-500/15 text-slate-700 dark:text-slate-200' : ''}
                    ${range && !start && !end ? 'text-primary-600 dark:text-primary-400' : ''}
                  `}>
                    {day}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const label = (() => {
    if (startDate && endDate) return `${formatDisplay(startDate)} – ${formatDisplay(endDate)}`;
    return 'Select date range';
  })();

  const popover = open && rect ? ReactDOM.createPortal(
    <div
      ref={popoverRef}
      className="fixed z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl p-5"
      style={{ top: rect.bottom + 8, left: Math.max(8, rect.left - 100) }}
    >
      <div className="flex gap-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setLeftMonth(m => addMonths(m, -1))}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div />
          </div>
          {renderMonth(leftMonth)}
        </div>
        <div className="w-px bg-slate-100 dark:bg-white/10" />
        <div>
          <div className="flex items-center justify-between mb-2">
            <div />
            <button
              onClick={() => setLeftMonth(m => addMonths(m, 1))}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {renderMonth(rightMonth)}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/10 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {selecting === 'start' ? 'Click to select start date' : 'Click to select end date'}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleClear}
            className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            disabled={!draft.start || !draft.end}
            className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-600 transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => {
          setDraft({ start: startDate, end: endDate });
          setSelecting('start');
          setOpen(o => !o);
        }}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:border-primary-400 transition-colors"
      >
        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
        <span>{label}</span>
      </button>
      {popover}
    </>
  );
};

export default DateRangePicker;
